import { useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { useDateNow, useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { useApiData } from 'lib/api';
import { getFormattedDate } from 'lib/common';
import { EmptyIndicator, LoadingIndicator } from 'components/common';
import { Box, BoxBackLink, BoxRow } from 'components/common/box';
import {
  Answer,
  TaskDropdownFilter,
  TaskSummary
} from 'components/common/tasks';
import { CoursesLanguageDropdown } from 'components/courses';

import type { GetTasksRes } from 'shared-types/responses';

const FollowTasksTask = ({ taskId }: { taskId: number }) => {
  const { dateNow } = useDateNow();

  const {
    messages,
    pageState: { course },
    theme
  } = useReduxState();

  const router = useRouter();

  const [openQuestionFilter, setOpenQuestionFilter] = useState<
    'all' | 'unreviewed'
  >('all');

  // get selected language version filter option from URL, default to 'all' if not set
  const currentLanguageVersion =
    typeof router.query.languageVersion === 'string'
      ? router.query.languageVersion
      : 'all';

  const { data, loading, updateData } = useApiData<GetTasksRes>({
    onlyIf: Boolean(course?.id && taskId),
    url:
      `/admin/courses/${course.id}/tasks` +
      `?id=${taskId}` +
      `&include=answers` +
      `&languageVersion=${currentLanguageVersion}`
  });

  const handleLanguageVersionFilter = (value: string) => {
    const query = { ...router.query };
    if (value === 'all') delete query.languageVersion;
    else query.languageVersion = value;

    router.replace({ query }, undefined, { shallow: true });
  };

  const handleOpenQuestionFilter = (value: 'all' | 'unreviewed', answers) => {
    setOpenQuestionFilter(value);

    if (value === 'all') return answers || [];
    return answers.filter((answer) => !answer.reviews.length);
  };

  const task = useMemo(() => data?.tasks?.[0] || null, [data]);

  const answers: GetTasksRes['tasks'][0]['answers'] = useMemo(() => {
    if (!task) return [];

    let filteredAnswers = task.answers || [];
    if (currentLanguageVersion !== 'all') {
      filteredAnswers = filteredAnswers.filter(
        (answer) => answer.courseLanguageVersion === currentLanguageVersion
      ) as GetTasksRes['tasks'][0]['answers'];
    }

    return handleOpenQuestionFilter(openQuestionFilter, filteredAnswers);
  }, [data, openQuestionFilter]);

  return loading ? (
    <LoadingIndicator />
  ) : (
    <div>
      <div className="task">
        <Box
          title={
            <BoxBackLink
              as={`/admin/courses/${course.slug}/follow/tasks`}
              href="/admin/courses/[courseSlug]/follow/tasks"
              text={messages.tasksAllTasks}
            />
          }
          padding={0}
        >
          <BoxRow borderColor={theme.lightGreyDark} padding="25px 25px 35px">
            <div className="chapter-title">
              {`${task.chapter.number}. ${task.chapter.title}`}
              {!task.chapter.publishDate ? (
                <div className="chapter-title-status">
                  {`(${messages.commonNotPublished})`}
                </div>
              ) : Date.parse(task.chapter.publishDate as string) > dateNow ? (
                <div className="chapter-title-status">
                  {`(${messages.commonWillPublish} ${getFormattedDate({
                    date: task.chapter.publishDate
                  })})`}
                </div>
              ) : null}
            </div>

            <TaskSummary task={task} />
          </BoxRow>
        </Box>

        <Box
          padding={0}
          title={messages.tasksAnswers}
          titleTooltip={
            Object.keys(course.languageVersions).length > 0
              ? messages.courseLanguageVersionsAnswersDisplayedLanguage
              : null
          }
          style={{ marginTop: 25 }}
          headerContent={
            <div className="filters">
              {task.type === 'openquestion' && (
                <TaskDropdownFilter
                  handleChange={(value: 'all' | 'unreviewed') =>
                    handleOpenQuestionFilter(value, answers)
                  }
                  options={[
                    {
                      text: messages.commonShowAll,
                      value: 'all'
                    },
                    {
                      text: messages.commonShowUnreviewed,
                      value: 'unreviewed'
                    }
                  ]}
                  value={openQuestionFilter}
                />
              )}

              {Object.keys(course.languageVersions).length > 0 && (
                <CoursesLanguageDropdown
                  category="lighter"
                  currentLanguage={currentLanguageVersion}
                  includeAllOption={true}
                  languageOptions={[
                    course.language,
                    ...Object.keys(course.languageVersions)
                  ]}
                  onChange={handleLanguageVersionFilter}
                />
              )}
            </div>
          }
          headerContentWrap={true}
        >
          {answers.length ? (
            answers.map((answer) => (
              <BoxRow
                borderColor={theme.lightGreyDark}
                key={answer.id}
                padding={25}
              >
                <div className="answer">
                  <Answer
                    answer={answer}
                    courseId={course.id}
                    courseSlug={course.slug}
                    events={answer.events}
                    isAdmin={true}
                    onDataChange={updateData}
                    reviews={answer.reviews}
                    showQuestion={false}
                    task={answer.answerLocalizedBlock}
                    user={answer.user}
                  />
                </div>
              </BoxRow>
            ))
          ) : (
            <EmptyIndicator
              icon={['far', 'file-alt']}
              title={messages.tasksNoAnswers}
            />
          )}
        </Box>
      </div>

      <style jsx>{`
        .chapter-title {
          color: ${theme.textColorLight};
          font-size: 1.6rem;
          font-weight: 700;
          margin: 0 5px 15px;
        }
        .chapter-title-status {
          color: ${theme.textColorLighter};
          display: inline;
          font-size: 1.3rem;
          font-weight: 400;
          margin-left: 7px;
          position: relative;
          top: -1px;
        }
        .filters {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          justify-content: flex-end;
        }
        .answers {
          max-width: 720px;
        }
        @media (max-width: ${breakpoints.md}) {
          .filters {
            align-items: flex-start;
          }
        }
      `}</style>
    </div>
  );
};

export default FollowTasksTask;
